import { useState, useEffect } from "react";
import { useAuth } from "@/lib/AuthContext";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { toast } from "sonner";
import { GearSix, FloppyDisk, ArrowCounterClockwise, ShieldCheck, ArrowsClockwise } from "@phosphor-icons/react";
import { AccessDenied } from "@/components/AccessDenied";
import { formatTz, resolveTz } from "@/lib/timezone";
import AdminSmsTab from "@/pages/AdminSmsTab";
import LlmUsageTab from "@/pages/LlmUsageTab";
import SecurityTab from "@/pages/SecurityTab";
import QuarantineRulesTab from "@/pages/admin/QuarantineRulesTab";
import RenewalsAndDriftTab from "@/pages/admin/RenewalsAndDriftTab";
import BookingInquiryTemplateTab from "@/pages/admin/BookingInquiryTemplateTab";
import BackupsPage from "@/pages/admin/BackupsPage";
import SystemHealthTab from "@/pages/admin/SystemHealthTab";
import { useSearchParams } from "react-router-dom";

// Embed the full Backups admin page inside the System Admin "Backups"
// tab (so it doesn't render duplicate page headers / breadcrumbs).
function BackupsTabRedirect() {
  return <BackupsPage />;
}

const ACTOR_COLORS = {
  eyvo_staff: "bg-blue-50 text-blue-700",
  client: "bg-purple-50 text-purple-700",
  lead: "bg-amber-50 text-amber-700",
  public: "bg-emerald-50 text-emerald-700",
  system: "bg-[#F5F5F5] text-[#525252]",
};
const ACTIVITY_COLORS = {
  login_success_password: "text-emerald-700",
  login_success_google: "text-emerald-700",
  login_failed: "text-red-600",
  login_blocked_disabled: "text-red-600",
  login_blocked_non_eyvo: "text-red-600",
  proposal_viewed: "text-blue-700",
  proposal_accepted: "text-emerald-700",
  proposal_rejected: "text-red-600",
};

function AuditTrail() {
  const { user } = useAuth();
  const tz = resolveTz(user);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actorType, setActorType] = useState("");
  const [activityType, setActivityType] = useState("");
  const [search, setSearch] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const params = { limit: 200 };
      if (actorType) params.actor_type = actorType;
      if (activityType) params.activity_type = activityType;
      if (search) params.search = search;
      const r = await api.get("/admin/activity-log", { params });
      setRows(r.data || []);
    } catch { toast.error("Failed to load audit trail"); }
    setLoading(false);
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  return (
    <Card className="border border-[#E5E5E5] rounded-none p-5" data-testid="audit-trail-card">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
        <h2 className="text-xs font-bold uppercase tracking-[0.15em] text-[#A3A3A3] flex items-center gap-1.5">
          <ShieldCheck size={14} /> External Activity Audit Trail
        </h2>
        <div className="flex items-center gap-3">
          <span className="text-[10px] text-[#A3A3A3] uppercase tracking-wider">Times in <span className="font-mono text-[#525252]" data-testid="audit-tz-label">{tz}</span></span>
          <Button onClick={load} disabled={loading} size="sm" className="bg-[#0055FF] hover:bg-[#003ECB] text-white font-bold rounded-none h-8 px-3 text-xs uppercase tracking-wider" data-testid="refresh-audit-btn">
            <ArrowsClockwise size={12} className="mr-1.5" /> Refresh
          </Button>
        </div>
      </div>
      <div className="flex flex-wrap gap-2 mb-4" data-testid="audit-filters">
        <select value={actorType} onChange={e => setActorType(e.target.value)} className="rounded-none border border-[#E5E5E5] h-8 px-2 text-xs" data-testid="audit-actor-filter">
          <option value="">All actor types</option>
          <option value="eyvo_staff">Eyvo staff</option>
          <option value="client">Client</option>
          <option value="lead">Lead</option>
          <option value="public">Public (share link)</option>
          <option value="system">System</option>
        </select>
        <select value={activityType} onChange={e => setActivityType(e.target.value)} className="rounded-none border border-[#E5E5E5] h-8 px-2 text-xs" data-testid="audit-activity-filter">
          <option value="">All activities</option>
          <option value="login_success_password">Login success (password)</option>
          <option value="login_success_google">Login success (Google)</option>
          <option value="login_failed">Login failed</option>
          <option value="login_blocked_disabled">Login blocked (disabled)</option>
          <option value="login_blocked_non_eyvo">Login blocked (non-eyvo Google)</option>
          <option value="proposal_viewed">Proposal viewed</option>
          <option value="proposal_accepted">Proposal accepted</option>
          <option value="proposal_rejected">Proposal rejected</option>
        </select>
        <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search name, IP, details…" className="rounded-none border border-[#E5E5E5] h-8 px-2 text-xs flex-1 min-w-[180px]" data-testid="audit-search-input" />
        <Button onClick={load} size="sm" className="bg-[#0A0A0A] text-white rounded-none h-8 px-3 text-xs uppercase tracking-wider">Apply</Button>
      </div>
      {loading ? (
        <div className="p-8 flex justify-center"><div className="w-6 h-6 border-2 border-[#0055FF] border-t-transparent animate-spin" /></div>
      ) : rows.length === 0 ? (
        <div className="p-8 text-center text-[#A3A3A3] text-sm">No activity matching these filters</div>
      ) : (
        <div className="overflow-x-auto border border-[#F5F5F5]" data-testid="audit-trail-table">
          <table className="w-full text-xs">
            <thead className="bg-[#F9FAFB] border-b border-[#E5E5E5]">
              <tr className="text-[10px] text-[#A3A3A3] font-bold uppercase tracking-wider">
                <th className="text-left px-3 py-2">Date / Time</th>
                <th className="text-left px-3 py-2">IP</th>
                <th className="text-left px-3 py-2">Actor</th>
                <th className="text-left px-3 py-2">Activity</th>
                <th className="text-left px-3 py-2">Lead / Client</th>
                <th className="text-left px-3 py-2">Details</th>
              </tr>
            </thead>
            <tbody>
              {rows.map(r => (
                <tr key={r.activity_id} className="border-b border-[#F5F5F5] hover:bg-[#F9FAFB]" data-testid={`audit-row-${r.activity_id}`}>
                  <td className="px-3 py-2 font-mono text-[11px] whitespace-nowrap">{r.activity_at ? formatTz(r.activity_at, tz) : ""}</td>
                  <td className="px-3 py-2 font-mono text-[11px] whitespace-nowrap">{r.ip_address || "—"}</td>
                  <td className="px-3 py-2">
                    <div className="flex flex-col">
                      <span className={`inline-block px-1.5 py-0.5 text-[9px] font-bold uppercase w-fit ${ACTOR_COLORS[r.actor_type] || "bg-[#F5F5F5] text-[#525252]"}`}>{r.actor_type}</span>
                      <span className="text-[11px] text-[#0A0A0A] mt-0.5 truncate max-w-[160px]" title={r.actor_name || ""}>{r.actor_name || "—"}</span>
                    </div>
                  </td>
                  <td className={`px-3 py-2 font-semibold ${ACTIVITY_COLORS[r.activity_type] || "text-[#0A0A0A]"}`}>{r.activity_type}</td>
                  <td className="px-3 py-2">
                    <div className="truncate max-w-[220px]" title={r.entity_name || ""}>{r.entity_name || "—"}</div>
                    {r.entity_type && <div className="text-[9px] text-[#A3A3A3] uppercase tracking-wider">{r.entity_type}</div>}
                  </td>
                  <td className="px-3 py-2 text-[#525252] truncate max-w-[260px]" title={r.details || ""}>{r.details || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
}

export default function SystemAdminPage() {
  const { user, hasRole } = useAuth();
  // `?tab=` deep link (the header health dot opens ?tab=health).
  const [searchParams] = useSearchParams();
  const [tab, setTab] = useState(searchParams.get("tab") || "config");
  useEffect(() => {
    const t = searchParams.get("tab");
    if (t) setTab(t);
  }, [searchParams]);
  const [variables, setVariables] = useState([]);
  const [template, setTemplate] = useState("");
  const [preview, setPreview] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [edits, setEdits] = useState({});
  const isAdmin = hasRole("platform_admin") || hasRole("delivery_admin");

  useEffect(() => {
    loadData();
  }, []);

  if (!isAdmin) return <AccessDenied module="System Administration" />;

  const loadData = async () => {
    setLoading(true);
    try {
      const [varsRes, notesRes] = await Promise.all([
        api.get("/admin/variables"),
        api.get("/admin/standard-notes-rendered"),
      ]);
      setVariables(varsRes.data);
      const editMap = {};
      varsRes.data.forEach(v => { editMap[v.variable_name] = v.default_value; });
      setEdits(editMap);
      setTemplate(notesRes.data.template || "");
      setPreview(notesRes.data.rendered || "");
    } catch (err) {
      toast.error("Failed to load system config");
    }
    setLoading(false);
  };

  const renderPreview = (editMap) => {
    let rendered = template;
    for (const [k, v] of Object.entries(editMap)) {
      rendered = rendered.replaceAll(`{{${k}}}`, v);
    }
    setPreview(rendered);
  };

  const updateVar = (name, value) => {
    const newEdits = { ...edits, [name]: value };
    setEdits(newEdits);
    renderPreview(newEdits);
  };

  const saveVariables = async () => {
    setSaving(true);
    try {
      const updates = Object.entries(edits).map(([variable_name, default_value]) => ({ variable_name, default_value }));
      await api.put("/admin/variables", updates);
      toast.success("Variables saved");
      loadData();
    } catch { toast.error("Failed to save"); }
    setSaving(false);
  };

  const saveTemplate = async () => {
    setSaving(true);
    try {
      await api.put("/admin/config/default_standard_notes", { config_value: template });
      toast.success("Template saved");
      loadData();
    } catch { toast.error("Failed to save"); }
    setSaving(false);
  };

  if (loading) return <div className="p-8 flex justify-center"><div className="w-8 h-8 border-2 border-[#0055FF] border-t-transparent animate-spin" /></div>;

  return (
    <div className="page-enter p-6 sm:p-8 space-y-6" data-testid="system-admin-page">
      <div>
        <h1 className="text-3xl sm:text-4xl font-black tracking-tighter text-[#0A0A0A]" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
          System Admin
        </h1>
        <p className="text-sm text-[#525252] mt-1">Proposal configuration, system variables, and external audit trail</p>
      </div>

      <div className="flex gap-0 border-b border-[#E5E5E5]" data-testid="admin-tabs">
        {[
          { id: "config", label: "Proposal Config" },
          { id: "audit", label: "External Audit Trail" },
          { id: "security", label: "Security" },
          { id: "quarantine", label: "Quarantine Rules" },
          { id: "renewals", label: "Renewals & Drift" },
          { id: "booking_inquiry", label: "Booking Inquiry Email" },
          { id: "sms", label: "SMS Console" },
          { id: "llm", label: "LLM Usage" },
          { id: "backups", label: "Backups" },
          ...(hasRole("platform_admin") ? [{ id: "health", label: "System Health" }] : []),
        ].map(t => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            className={`px-4 py-2 text-xs font-bold uppercase tracking-wider border-b-2 transition-colors ${tab === t.id ? "border-[#0055FF] text-[#0055FF]" : "border-transparent text-[#525252] hover:text-[#0A0A0A]"}`}
            data-testid={`admin-tab-${t.id}`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === "audit" && <AuditTrail />}
      {tab === "security" && <SecurityTab />}
      {tab === "quarantine" && <QuarantineRulesTab />}
      {tab === "renewals" && <RenewalsAndDriftTab />}
      {tab === "booking_inquiry" && <BookingInquiryTemplateTab />}
      {tab === "sms" && <AdminSmsTab />}
      {tab === "llm" && <LlmUsageTab />}
      {tab === "backups" && <BackupsTabRedirect />}
      {tab === "health" && hasRole("platform_admin") && <SystemHealthTab />}

      {tab === "config" && (
        <>
      {/* Email-template export — for hand-off to an external design vendor */}
      <Card className="border border-[#E5E5E5] rounded-none p-5" data-testid="email-export-card">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div>
            <h2 className="text-xs font-bold uppercase tracking-[0.15em] text-[#A3A3A3] flex items-center gap-1.5">
              <FloppyDisk size={14} /> Outbound email templates
            </h2>
            <p className="text-xs text-[#525252] mt-1 max-w-2xl leading-snug">
              Download a ZIP of the exact HTML that hits the inbox when we send
              a proposal (initial + reminder, both <code className="bg-[#F5F5F5] px-1">cid:</code> and
              base64-inline-logo variants), plus a README that explains what's safe to change
              and what must be preserved for Outlook / Gmail / Hotmail compatibility.
              Hand the ZIP to your external email-design team — they can crack it
              open in any browser or pipe it through Litmus / Email on Acid.
            </p>
          </div>
          <Button
            onClick={async () => {
              try {
                const res = await api.get(
                  "/admin/email-templates/proposal-bundle.zip",
                  { responseType: "blob" },
                );
                const url = window.URL.createObjectURL(res.data);
                const a = document.createElement("a");
                a.href = url;
                a.download = "eyvo-proposal-email-bundle.zip";
                document.body.appendChild(a);
                a.click();
                a.remove();
                window.URL.revokeObjectURL(url);
              } catch (e) {
                toast.error(e.response?.data?.detail || "Download failed");
              }
            }}
            size="sm"
            className="bg-[#0055FF] hover:bg-[#003ECB] text-white font-bold rounded-none h-9 px-4 text-xs uppercase tracking-wider"
            data-testid="download-email-bundle-btn"
          >
            <FloppyDisk size={12} className="mr-1.5" /> Download bundle
          </Button>
        </div>
      </Card>

      {/* Variables */}
      <Card className="border border-[#E5E5E5] rounded-none p-5">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xs font-bold uppercase tracking-[0.15em] text-[#A3A3A3] flex items-center gap-1.5">
            <GearSix size={14} /> System Variables
          </h2>
          <Button onClick={saveVariables} disabled={saving} size="sm" className="bg-[#0055FF] hover:bg-[#003ECB] text-white font-bold rounded-none h-8 px-3 text-xs uppercase tracking-wider" data-testid="save-variables-btn">
            <FloppyDisk size={12} className="mr-1.5" /> Save Variables
          </Button>
        </div>
        <p className="text-xs text-[#525252] mb-4">These values are substituted into the standard notes template when a new proposal is created. Use <code className="bg-[#F5F5F5] px-1 py-0.5 text-[10px]">{"{{VARIABLE_NAME}}"}</code> in the template.</p>
        <div className="bg-blue-50 border border-blue-200 px-3 py-2 mb-4 text-[11px] text-blue-900">
          <span className="font-bold uppercase tracking-wider">Built-in auto variable:</span>{" "}
          <code className="bg-white px-1 py-0.5 font-mono">{"{{CURRENCY_SYMBOL}}"}</code> is resolved automatically per proposal from the client or lead's country (e.g. $, £, €). No manual setting required.
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {variables.map(v => (
            <div key={v.variable_name} className="flex items-center gap-3 border border-[#F5F5F5] p-3">
              <div className="flex-1 min-w-0">
                <p className="text-[10px] font-bold uppercase tracking-wider text-[#0055FF] font-mono">{`{{${v.variable_name}}}`}</p>
                <p className="text-[10px] text-[#A3A3A3] mt-0.5 truncate">{v.description}</p>
              </div>
              <Input
                value={edits[v.variable_name] || ""}
                onChange={e => updateVar(v.variable_name, e.target.value)}
                className="rounded-none border-[#E5E5E5] h-8 w-32 text-sm text-right font-mono"
                data-testid={`var-${v.variable_name}`}
              />
            </div>
          ))}
        </div>
      </Card>

      {/* Standard Notes Template */}
      <Card className="border border-[#E5E5E5] rounded-none p-5">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xs font-bold uppercase tracking-[0.15em] text-[#A3A3A3]">Standard Notes Template</h2>
          <Button onClick={saveTemplate} disabled={saving} size="sm" className="bg-[#0055FF] hover:bg-[#003ECB] text-white font-bold rounded-none h-8 px-3 text-xs uppercase tracking-wider" data-testid="save-template-btn">
            <FloppyDisk size={12} className="mr-1.5" /> Save Template
          </Button>
        </div>
        <p className="text-xs text-[#525252] mb-3">Edit the template below. Variables in <code className="bg-[#F5F5F5] px-1 py-0.5 text-[10px]">{"{{DOUBLE_BRACES}}"}</code> will be replaced with their values when a new proposal is created.</p>
        <textarea
          value={template}
          onChange={e => { setTemplate(e.target.value); const t = e.target.value; let r = t; for (const [k, v] of Object.entries(edits)) { r = r.replaceAll(`{{${k}}}`, v); } setPreview(r); }}
          rows={14}
          className="w-full border border-[#E5E5E5] px-3 py-2 text-xs font-mono focus:outline-none focus:border-[#0055FF] leading-relaxed"
          data-testid="notes-template-input"
        />
      </Card>

      {/* Preview */}
      <Card className="border border-[#E5E5E5] rounded-none p-5">
        <h2 className="text-xs font-bold uppercase tracking-[0.15em] text-[#A3A3A3] mb-3">Preview (as it will appear on new proposals)</h2>
        <div className="bg-[#FAFAFA] border border-[#E5E5E5] p-4">
          <pre className="text-xs text-[#525252] whitespace-pre-wrap leading-relaxed font-sans" data-testid="notes-preview">{preview}</pre>
        </div>
      </Card>
        </>
      )}
    </div>
  );
}
